import countries from "./countries.ts";
import {isToday} from "date-fns/isToday";
import i18n from "../i18n";
import {formatDate as fnsFormatDate} from "date-fns/format";
import {isYesterday} from "date-fns/isYesterday";
import {isThisYear} from "date-fns/isThisYear";

const URL_REGEX = /(?<!\]\(|\]\(<|href=["']|src=["'])(https?:\/\/[^\s<>"')]+)(?![^[]*\]\))/g;

function stripTrailingPunctuation(url: string): string {
  return url.replace(/[.,;:!?]+$/g, "");
}

function splitByCodeBlocks(content: string): string[] {
  return content.split(/(```[\s\S]*?```)/g);
}

function isCodeBlock(part: string): boolean {
  return part.startsWith("```") && part.endsWith("```");
}

export function formatRawLinks(content: string): string {
  const parts = splitByCodeBlocks(content);

  return parts
    .map((part) => {
      if (isCodeBlock(part)) {
        return part;
      }

      return part.replace(URL_REGEX, (url: string) => {
        const cleanUrl = stripTrailingPunctuation(url);
        const trailing = url.slice(cleanUrl.length);

        return `[${cleanUrl}](${cleanUrl})${trailing}`;
      });
    })
    .join("");
}

export const formatPhoneNumber = (
  phoneNumber: string,
  country?: string
): string => {
  const countryCode = countries[country ?? 'CM']?.code;
  if (phoneNumber?.startsWith('+')) {
    phoneNumber = phoneNumber.substring(1);
  }
  if (countryCode && phoneNumber?.startsWith(countryCode)) {
    return phoneNumber.substring(countryCode.length);
  }
  return phoneNumber;
};

export const formatDateTime = (date: Date) => {
  if (isToday(date)) {
    return i18n.t('Today_at', { time: fnsFormatDate(date, 'p') });
  }
  if (isYesterday(date)) {
    return i18n.t('Yesterday_at', { time: fnsFormatDate(date, 'p') });
  }

  if (isThisYear(date)) {
    return i18n.t('Date_format', {
      day: fnsFormatDate(date, 'MMM, dd'),
      time: fnsFormatDate(date, 'p'),
    });
  }
  return i18n.t('Date_format', {
    day: fnsFormatDate(date, 'PP'),
    time: fnsFormatDate(date, 'p'),
  });
};

export const formatNumberAbbr = (
  value: number,
  options?: { decimal?: number; currency?: string }
) => {
  if (value > 1000000) {
    return `${i18n.t('intlNumber', { value: value / 1000000 })}M`;
  } else if (value > 1000) {
    return `${i18n.t('intlNumber', { value: value / 1000 })}K`;
  }
  return i18n.t('intlNumber', { value: value });
};
